import React, { createContext, useContext, useState, useEffect } from 'react'
import dynamic from 'next/dynamic'
import '../src/scss/layouts/container.scss'
import '../src/scss/layouts/flex.scss'
import '../src/scss/layouts/grid.scss'
import '../public/tabler-icons/tabler-icons.min.css'
import '../src/scss/main.scss'
import global from '../src/data/global-data.json'
import { MicroCopyContextProvider } from '@/src/context/MicroCopyContext'
import { NavigationProvider } from '@/src/context/NavigationContext'
import useSaveScrollPosition from '@/src/storybook/hooks/useSaveScrollPosition'
import { LiveChatLoaderProvider } from 'react-live-chat-loader'
import Analytics from '@/src/components/analytics/Analytics'
import Header from '@/src/components/header'
import { PageWrapper } from '@/src/components'
import Footer from '@/src/components/footer'

// Dynamically import client-only components to prevent hydration errors
const LoadChatButton = dynamic(() => import('@/src/components/intercom/LoadChatButton'), {
  ssr: false
})

// Client-only wrapper for LiveChatLoaderProvider to prevent hydration errors
const ClientOnlyChatProvider = ({ children }) => {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return <>{children}</>
  }

  return (
    <LiveChatLoaderProvider providerKey="aurjyqxf" provider="intercom">
      {children}
      <LoadChatButton />
    </LiveChatLoaderProvider>
  )
}

const MicroCopyContext = createContext()
export const useMicroCopyData = () => useContext(MicroCopyContext)

function MyApp({ Component, pageProps, router }) {
  useSaveScrollPosition(router)

  return (
    <>
      <ClientOnlyChatProvider>
        <MicroCopyContextProvider>
          <NavigationProvider>
            <GlobalProvider>
              <PageWrapper
                currentMarket={pageProps.market}
                pageContext={{
                  pageId: pageProps.page?.id || 'not-found',
                  locale: pageProps.locale || null,
                  market: pageProps.market || null,
                  integrations: [], //TODO: add integrations
                  articleCategories: pageProps.articleCategories || [],
                  articles: pageProps.articles || []
                }}
              >
                {pageProps.header && (
                  <Header
                    headerData={pageProps.header}
                    isGlobalSite={true}
                    slugPrefix={pageProps.slugPrefix}
                    marketsSlugs={pageProps.marketsSlugs || {}}
                    allLangSlugs={pageProps.page?.allLangSlugs || []}
                    langSlugs={pageProps.page?.langSlugs || {}}
                    hideTopBar={pageProps.page?.minimalFooter || pageProps.page?.minimalHeader}
                    minimalHeader={pageProps.page?.minimalHeader}
                    minimalHeaderAction={pageProps.page?.minimalHeaderAction}
                  />
                )}
                <Component {...pageProps} />
                <Footer
                  footerMainNavigation={pageProps.footer?.mainNavigation || []}
                  footerSocialMedia={pageProps.footer?.socialMedia || []}
                  footerLegalNavigation={pageProps.footer?.legalNavigation || []}
                  actions={pageProps.footer?.actions || []}
                  minimalFooter={pageProps.page?.minimalFooter || null}
                  footerText={null}
                />
              </PageWrapper>
            </GlobalProvider>
          </NavigationProvider>
        </MicroCopyContextProvider>
      </ClientOnlyChatProvider>

      <Analytics />
    </>
  )
}

export default MyApp

const GlobalContext = createContext()
export const useGlobalData = () => useContext(GlobalContext)
export const GlobalProvider = ({ children }) => {
  const [globalData, setGlobalData] = useState({
    ...global,
    isSideDrawerOpen: false
  })

  const toggleSideDrawer = (state = null) => {
    setGlobalData((prev) => ({
      ...prev,
      isSideDrawerOpen: typeof state === 'boolean' ? state : !prev.isSideDrawerOpen
    }))
  }

  return (
    <GlobalContext.Provider
      value={{
        ...globalData,
        toggleSideDrawer
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}
